import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useState,
} from 'react';

import { stringify as queryStringify } from 'querystring';
import { AxiosError } from 'axios';
import { useRouter } from 'next/router';

import { routes } from '@routes';

import { logout } from '@api/auth';

import { storage } from '@utils/local-storage';
import { isOnLoginPage } from '@utils/location';
import { link } from '@utils/link';

import { createLoginRedirectUrl } from './utils';
import { LoginReason, Role, TokenType } from './types';

export type LoginRedirectParams = {
  withLogoutRequest?: boolean;
  reason: LoginReason;
};

export type AuthContext = {
  account?: AccountAuthPayload;
  isChecked?: boolean;
  setChecked: (value: boolean) => void;
  setAccount: (account: AccountAuthPayload) => void;
  handleLoginRedirect: (params: LoginRedirectParams) => Promise<void>;
  handleLoggedInRedirect: () => void;
};

export type AccountAuthPayload = {
  type: Exclude<TokenType, 'SERVICE' | 'REFRESH_TOKEN'>;
  sessionId: string;
  roles: Exclude<Role, 'SERVICE'>[];
  accountId: string;
  iss?: string;
  email?: string;
  pipedrivePersonId?: number;
  avatar?: Record<string, any>;
  fullname?: string;
} & TokenParams;

type TokenParams = {
  exp: number;
};

export const AuthContext = createContext<AuthContext>({
  setAccount: () => {
    throw Error('User context not inited yet');
  },
  setChecked: () => {
    throw Error('User context not inited yet');
  },
  handleLoginRedirect: async () => {
    throw Error('User context not inited yet');
  },
  handleLoggedInRedirect: () => {
    throw Error('User context not inited yet');
  },
});

export const AuthContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const router = useRouter();
  const [isChecked, setChecked] = useState(false);
  const [account, setAccount] = useState<AuthContext['account']>();

  const handleLoginRedirect = useCallback(
    async ({ withLogoutRequest, reason }: LoginRedirectParams) => {
      setAccount(undefined);
      storage.setItems({ isLoggedIn: false });
      if (withLogoutRequest)
        await logout().catch((e) => {
          if ((e as AxiosError).isAxiosError) {
            const axiosError = e as AxiosError;
            if (axiosError.response?.status === 400) {
              // account is without access token (probably already logged out)
              return;
            }
          }
          throw e;
        });

      // api client is calling this method every time it detects token error,
      // and login page is calling refresh endpoint which could throw this error.
      if (isOnLoginPage()) return;

      const redirectUrl = createLoginRedirectUrl(
        queryStringify(router.query),
        new URL(window.location.href),
        reason,
      );
      router.replace(redirectUrl);
    },
    [account, router],
  );
  const handleLoggedInRedirect = useCallback(() => {
    if (isOnLoginPage()) {
      let parsedRedirect = link(routes.homepage);

      if (router.query.r) {
        try {
          parsedRedirect = JSON.parse(router.query.r.toString());
        } catch (err) {
          // parsing of the query failed, pass
        }
      }

      router.replace(parsedRedirect.href, parsedRedirect.as);
    } else {
      router.reload();
    }
  }, [router]);

  const handleSetAccount = (account: AccountAuthPayload) => {
    setAccount(account);
    // this will sync logged-in state with other tabs,
    storage.setItems({ isLoggedIn: true });
  };

  return (
    <AuthContext.Provider
      value={{
        setAccount: handleSetAccount,
        isChecked,
        setChecked,
        handleLoginRedirect: handleLoginRedirect,
        handleLoggedInRedirect: handleLoggedInRedirect,
        account,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
